import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { BuilderErrorBanner } from '@/components/builder/BuilderErrorBanner';
import { inferLimitVariant, useEntitlement } from '@/hooks/useEntitlement';
import { apiFetch } from '@/lib/api-client';

interface BuilderGateProps {
  children: ReactNode;
}

const RESEND_SUCCESS_DURATION_MS = 3000;
const RESEND_RATE_LIMIT_MS = 60000;

/**
 * Wraps a builder form and renders it only when all 3 pre-submit gates pass:
 *   1. emailVerified
 *   2. hasChildren
 *   3. canCreateStory
 * Gate failures render a BuilderErrorBanner instead of the form, priority
 * order highest→lowest (email > child > entitlement).
 */
export function BuilderGate({ children }: BuilderGateProps) {
  const gate = useEntitlement();

  // Email-resend state — only relevant while the email-unverified banner
  // renders. Lives here (not in the banner) so the banner stays
  // pure/presentational and a re-render doesn't reset timers.
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccessUntil, setResendSuccessUntil] = useState<number | null>(null);
  const [rateLimitedUntil, setRateLimitedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Tick every 250ms while either timestamp is in the future, so the derived
  // resendSuccess/rateLimited flags flip as time passes. setInterval callback
  // setState is async — not flagged by react-hooks/set-state-in-effect.
  useEffect(() => {
    if (resendSuccessUntil === null && rateLimitedUntil === null) return;
    const interval = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (resendSuccessUntil !== null && current >= resendSuccessUntil) {
        setResendSuccessUntil(null);
      }
      if (rateLimitedUntil !== null && current >= rateLimitedUntil) {
        setRateLimitedUntil(null);
      }
    }, 250);
    return () => window.clearInterval(interval);
  }, [resendSuccessUntil, rateLimitedUntil]);

  const resendSuccess = resendSuccessUntil !== null && now < resendSuccessUntil;
  const rateLimited = rateLimitedUntil !== null && now < rateLimitedUntil;

  const handleResend = async () => {
    if (resendLoading || rateLimited) return;
    setResendLoading(true);
    try {
      await apiFetch('/api/auth/request-verification', {
        method: 'POST',
        body: {},
      });
      setResendSuccessUntil(Date.now() + RESEND_SUCCESS_DURATION_MS);
      setRateLimitedUntil(Date.now() + RESEND_RATE_LIMIT_MS);
    } catch (err) {
      // 400 ("already verified"): stale state, will resolve on next gate fetch.
      // Network / 5xx: silent, user retries. Don't crash the banner.
      console.error('[BuilderGate] resend failed:', err);
    } finally {
      setResendLoading(false);
    }
  };

  if (gate.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 aria-hidden className="h-8 w-8 animate-spin text-dream-navy/40" />
      </div>
    );
  }

  // 401: api-client already ran refresh + failed. AuthStore will redirect.
  // Render nothing in the meantime to avoid flashing gate-banner content.
  if (gate.error === 'unauthorized') {
    return null;
  }

  if (gate.error) {
    return (
      <p className="py-8 text-center text-sm text-dream-navy/70">
        Etwas ist schiefgelaufen. Bitte lade die Seite neu.
      </p>
    );
  }

  if (!gate.emailVerified) {
    return (
      <BuilderErrorBanner
        variant="email-unverified"
        onResend={handleResend}
        resendLoading={resendLoading}
        resendSuccess={resendSuccess}
        rateLimited={rateLimited}
      />
    );
  }

  if (!gate.hasChildren) {
    return <BuilderErrorBanner variant="no-child" />;
  }

  if (!gate.canCreateStory) {
    if (!gate.entitlement) {
      return (
        <p className="py-8 text-center text-sm text-dream-navy/70">
          Etwas ist schiefgelaufen. Bitte lade die Seite neu.
        </p>
      );
    }
    const inferred = inferLimitVariant(gate.entitlement);
    if (inferred.variant === 'limit-trial') {
      return <BuilderErrorBanner variant="limit-trial" />;
    }
    if (inferred.variant === 'limit-subscription-month') {
      return (
        <BuilderErrorBanner
          variant="limit-subscription-month"
          formattedEndDate={inferred.formattedEndDate}
        />
      );
    }
    return (
      <BuilderErrorBanner
        variant="limit-subscription-expired"
        formattedEndDate={inferred.formattedEndDate}
      />
    );
  }

  return <>{children}</>;
}
